import {
  IArnoFieldUsage,
  IArnoFieldUsageDetail,
  ArnoEvidenceStatus,
  ArnoFieldState,
  ArnoMappingKind,
} from '../../hooks/reactQuerys/ArnoAdminQueries';

/**
 * Beschriftungen und abgeleitete Aussagen des Verwendungsnachweises (#1245).
 *
 * Die Zahlen kommen gemessen aus dem Backend; hier steht nur, was sie für einen
 * Menschen bedeuten. Bewusst keine Prozentbalken und keine „Zuversicht": eine
 * Zuordnung ist belegt oder sie ist es nicht.
 */

export const EVIDENCE_LABEL: Record<ArnoEvidenceStatus, string> = {
  PROVEN: 'Belegt',
  VERY_STRONG_EVIDENCE: 'Sehr starkes Indiz',
  AMBIGUOUS: 'Mehrdeutig',
  UNKNOWN: 'Unbekannt',
};

export const STATE_LABEL: Record<ArnoFieldState, string> = {
  CONFIRMED: 'Entschieden',
  READY_TO_CONFIRM: 'Bestätigungsreif',
  NEEDS_EVIDENCE: 'Bedeutung offen',
  NO_DINO_SOURCE: 'DiNo hat die Daten nicht',
};

export const MAPPING_KIND_LABEL: Record<ArnoMappingKind, string> = {
  DIRECT: 'Direktes Feld',
  COMPUTED: 'Berechnetes Feld',
  NONE: 'Kein Ziel',
};

export type ChipColor =
  | 'default'
  | 'primary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info';

export const stateChip = (
  state: ArnoFieldState
): { label: string; color: ChipColor } => {
  switch (state) {
    case 'CONFIRMED':
      return { label: STATE_LABEL.CONFIRMED, color: 'success' };
    case 'READY_TO_CONFIRM':
      return { label: STATE_LABEL.READY_TO_CONFIRM, color: 'primary' };
    case 'NO_DINO_SOURCE':
      // Kein Fehler, sondern ein Befund: die Bedeutung ist klar, DiNo hat sie nur nicht.
      return { label: STATE_LABEL.NO_DINO_SOURCE, color: 'default' };
    default:
      return { label: STATE_LABEL.NEEDS_EVIDENCE, color: 'warning' };
  }
};

/** Darf diese Zeile mit einem Klick bestätigt werden? Nur belegt + Ziel + noch offen. */
export const isReadyToConfirm = (row: IArnoFieldUsage): boolean =>
  row.state === 'READY_TO_CONFIRM' &&
  row.evidenceStatus === 'PROVEN' &&
  Boolean(row.candidate);

/**
 * Ein Satz zur Hebelwirkung — die Zahl, die die Reihenfolge der Arbeit bestimmt.
 * Ohne blockierte Bedingungen bringt eine Zuordnung dieser Migration nichts.
 */
export const impactSentence = (row: IArnoFieldUsage): string => {
  if (row.blockedIfs === 0) {
    return row.safeIfs > 0
      ? `${row.safeIfs} Bedingungen sind dadurch bereits übernehmbar.`
      : 'Dieses Feld blockiert derzeit keine Bedingung.';
  }
  const blocks = `${row.blockCount} Baustein${row.blockCount === 1 ? '' : 'en'}`;
  const templates = `${row.templateCount} Rechtsgeschäftsart${row.templateCount === 1 ? '' : 'en'}`;
  return `${row.blockedIfs} Word-Bedingungen in ${blocks}, erreichbar aus ${templates}.`;
};

/**
 * Was eine Zuordnung heute wirklich bewirken würde.
 *
 * Eine Bedingung wird im Klon nur übersetzt, wenn auch jede äußere Bedingung über ihr
 * übersetzt wird. Deshalb stehen hier zwei Zahlen nebeneinander: wie viele Stellen das Feld
 * blockiert — und wie viele davon der Klon heute überhaupt erreicht. Sind es null, ist die
 * Zuordnung nicht falsch, sie wirkt nur noch nicht.
 */
export const reachSentence = (row: IArnoFieldUsage): string => {
  if (row.blockedIfs === 0) {
    return '';
  }
  if (row.blockedReachableIfs === 0) {
    return 'Davon erreicht der Klon heute keine: alle liegen unter einer äußeren Bedingung, die selbst offen ist.';
  }
  if (row.blockedReachableIfs === row.blockedIfs) {
    return 'Alle davon würden sofort im Klon ankommen.';
  }
  return `Davon kommen ${row.blockedReachableIfs} sofort im Klon an; ${
    row.blockedIfs - row.blockedReachableIfs
  } liegen unter einer äußeren Bedingung, die selbst offen ist.`;
};

/** Welche äußere Bedingung hält die Stellen dieses Feldes auf — als lesbare Zeilen. */
export const ancestorBlockers = (
  detail: IArnoFieldUsageDetail | undefined,
  limit = 5
): { label: string; count: number }[] =>
  (detail?.detail?.ancestorBlockers ?? [])
    .slice(0, limit)
    .map(([label, count]) => ({ label, count }));

/** Die häufigsten Vergleichswerte als lesbare Zeile; der leere Wert wird benannt. */
export const valueSummary = (
  values: [string, number][] | undefined,
  limit = 6
): string =>
  (values ?? [])
    .slice(0, limit)
    .map(([value, count]) => `${value === '' ? '(leer)' : value} (${count})`)
    .join(' · ');

/** Gegenüberstellung der Zweigtexte — daran liest man die Bedeutung ab. */
export interface IBranchPair {
  trueText: string;
  falseText: string;
}

export const branchPairs = (
  detail: IArnoFieldUsageDetail | undefined,
  limit = 6
): IBranchPair[] => {
  const yes = detail?.detail?.trueTexts ?? [];
  const no = detail?.detail?.falseTexts ?? [];
  const rows: IBranchPair[] = [];
  for (
    let index = 0;
    index < Math.min(limit, Math.max(yes.length, no.length));
    index += 1
  ) {
    rows.push({
      trueText: yes[index]?.[0] ?? '',
      falseText: no[index]?.[0] ?? '',
    });
  }
  return rows;
};

/** Summe der Bedingungen, die ein Stapel freischalten würde — nur offene Zeilen zählen. */
export const batchGain = (rows: IArnoFieldUsage[]): number =>
  rows
    .filter(row => row.state !== 'CONFIRMED')
    .reduce((total, row) => total + row.blockedIfs, 0);

/** Derselbe Stapel, aber nur die Stellen, die der Klon heute erreicht. */
export const batchReach = (rows: IArnoFieldUsage[]): number =>
  rows
    .filter(row => row.state !== 'CONFIRMED')
    .reduce((total, row) => total + (row.blockedReachableIfs ?? 0), 0);

/** „7 Felder erklären 50 % der blockierten Bedingungen" — als Satz, nicht als Balken. */
export const paretoSentence = (
  fields: number | undefined,
  blockedIfs: number | undefined,
  share: number
): string =>
  fields && blockedIfs
    ? `${fields} Felder erklären ${share} % der blockierten Bedingungen (${blockedIfs}).`
    : 'Noch nicht gemessen.';
